import { Fragment } from "react"
import {
	Alert,
	Badge,
	Box,
	Button,
	Heading,
	Icons,
	Inline,
	Spinner,
	Stack,
	Text,
	Time,
	Tooltip,
} from "@sembark-travel/ui-base"
import { useXHR } from "@sembark-travel/xhr"
import useSWR, { mutate } from "swr"
import {
	TCancellationPolicy,
	TCancellationPolicyCondition,
	TCancellationPolicyDetail,
	TCancellationPolicyRule,
	cancellationPoliciesXHR,
} from "./store"
import {
	EditCancellationPolicyInDialog,
	PenaltyTimelineDisplay,
} from "./NewItem"
import { Markdown } from "@sembark-travel/ui-markdown"
import {
	diffPolicyRules,
	formatPolicyConditionLabel,
	policyVersionToFormValues,
	TPolicyRuleDiffEntry,
} from "./utils"
import {
	diffTone,
	formatDateRangeRuleLabel,
	isBaseDateRangeRule,
	type TDiffSide,
	type TDiffStatus,
} from "../shared/policyDiffUtils"
import {
	DiffPill,
	VersionHistoryDialog as GenericVersionHistoryDialog,
} from "../shared/Policy"
import { formatDateRange } from "../shared/policies"
import { parseDate } from "@sembark-travel/datetime-utils"
import { useNavigate, useSearchParams } from "../router-utils"

export function CancellationPolicyItemDetails({
	detailId,
	name: detailName,
	isDisabled = false,
	isDefault = false,
	versionId,
	version,
}: {
	detailId: number
	name: string
	isDisabled?: boolean
	isDefault?: boolean
	versionId?: number
	version?: number
}) {
	const navigate = useNavigate()
	const [, setSearchParams] = useSearchParams()
	const hasMultipleVersions = Number(version) > 1

	return (
		<Stack>
			<Inline
				paddingX="2"
				paddingY="4"
				gap="2"
				alignItems="center"
				borderBottomWidth="1"
			>
				<Box
					rounded="md"
					paddingX="2"
					paddingY="1"
					cursor="pointer"
					bgColor={{ hover: "gray2" }}
					onClick={() => navigate(-1)}
				>
					<Icons.ArrowLeft size="5" />
				</Box>
				<Inline gap="3" alignItems="center" flexWrap="wrap">
					<Heading as="h4">
						{detailName || "Cancellation Policy Details"}
					</Heading>
					{version && versionId ? (
						<VersionHistoryDialog
							detailId={detailId}
							currentVersionId={versionId}
							currentVersion={version}
						>
							{({ open }) => (
								<Box
									paddingX="1"
									rounded="md"
									backgroundColor={hasMultipleVersions ? "gray1" : "inset"}
									cursor={hasMultipleVersions ? "pointer" : "default"}
									onClick={hasMultipleVersions ? open : undefined}
									title={
										hasMultipleVersions ? "Version History" : "First version"
									}
								>
									<Text color="muted" fontWeight="semibold">
										v{version}
									</Text>
								</Box>
							)}
						</VersionHistoryDialog>
					) : null}
					{isDefault ? <Badge warning>Default</Badge> : null}
				</Inline>
			</Inline>
			{isDefault ? (
				<Box paddingX="6" paddingTop="4">
					<Alert status="warning">
						This policy is automatically applied when no other Cancellation
						policy is specified. e.g. If a Hotel does not set another
						Cancellation policy explicitly, then this policy gets applied for
						that hotel.
					</Alert>
				</Box>
			) : null}
			{versionId ? (
				<PolicyVersionDetail
					versionId={versionId}
					detailId={detailId}
					detailName={detailName}
					isDisabled={isDisabled}
					isDefault={isDefault}
					onVersionCreated={(detail) => {
						if (!detail.latest_policy) return
						const nextVersionId = detail.latest_policy.id
						const nextVersion = detail.latest_policy.version
						setSearchParams(
							(prev) => {
								prev.set("name", detail.name)
								prev.set("version_id", String(nextVersionId))
								prev.set("version", String(nextVersion))
								return prev
							},
							{ replace: true }
						)
						// The version history dialog reads this list, and it now
						// has one more entry.
						mutate(
							`/cancellation-policy-heads/${detailId}/cancellation-policies`
						)
					}}
				/>
			) : (
				<Stack
					padding="6"
					justifyContent="center"
					alignItems="center"
					style={{ minHeight: "20rem" }}
				>
					<Text color="muted" fontWeight="semibold">
						No Policy Found.
					</Text>
				</Stack>
			)}
		</Stack>
	)
}

function PolicyVersionDetail({
	versionId,
	detailId,
	detailName,
	isDisabled,
	isDefault,
	onVersionCreated,
}: {
	versionId: number
	detailId: number
	detailName: string
	isDisabled: boolean
	isDefault: boolean
	onVersionCreated?: (detail: TCancellationPolicyDetail) => void
}) {
	const xhr = useXHR()
	const {
		data: policy,
		error,
		isLoading,
		isValidating,
	} = useSWR<TCancellationPolicy, Error>(
		`/cancellation-policies/${versionId}`,
		() => cancellationPoliciesXHR(xhr).getVersionById(versionId)
	)

	if (isLoading) {
		return (
			<Stack
				padding="6"
				justifyContent="center"
				alignItems="center"
				style={{ minHeight: "20rem" }}
			>
				<Spinner />
			</Stack>
		)
	}
	if (error) {
		return (
			<Box padding="6">
				<Alert status="error">
					{error.message || "Failed to load policy details. Please try again."}
				</Alert>
			</Box>
		)
	}
	if (!policy) {
		return (
			<Stack
				padding="6"
				justifyContent="center"
				alignItems="center"
				style={{ minHeight: "20rem" }}
			>
				<Text color="muted" fontWeight="semibold">
					No Policy Found.
				</Text>
			</Stack>
		)
	}

	const baseRule = policy.rules.find(isBaseDateRangeRule)
	const dateRules = policy.rules.filter((r) => !isBaseDateRangeRule(r))

	return (
		<Stack gap="4" paddingX="6" paddingY="4">
			<Inline
				gap="8"
				flexWrap="wrap"
				alignItems="center"
				justifyContent="between"
			>
				<Inline gap="8" flexWrap="wrap" alignItems="center">
					<Stack gap="px">
						<Text fontSize="sm">Last updated on</Text>
						<Time color="muted" timestamp={policy.created_at} dateFormat />
					</Stack>
					<Stack gap="px">
						<Text fontSize="sm">Last updated by</Text>
						<Text color="muted">{policy.created_by?.name}</Text>
					</Stack>
					{isValidating ? <Icons.Spinner size="6" title="Loading..." /> : null}
				</Inline>
				{isDisabled ? null : (
					<EditCancellationPolicyInDialog
						detailId={detailId}
						onSuccess={(updatedDetail) => {
							onVersionCreated?.(updatedDetail)
						}}
						isDefault={isDefault}
					>
						{({ onClick }) => (
							<Button
								status="primary"
								level="primary"
								onClick={() =>
									onClick(policyVersionToFormValues(detailName, policy))
								}
							>
								Edit policy
							</Button>
						)}
					</EditCancellationPolicyInDialog>
				)}
			</Inline>
			{baseRule ? <PolicyRuleCard rule={baseRule} isBase /> : null}
			{dateRules.map((rule) => (
				<PolicyRuleCard key={rule.id} rule={rule} isBase={false} />
			))}
			{policy.additional_info ? (
				<Stack gap="2">
					<Text fontWeight="semibold">Additional information</Text>
					<Markdown>{policy.additional_info}</Markdown>
				</Stack>
			) : null}
		</Stack>
	)
}

export function CancellationPolicyVersionView({
	versionId,
}: {
	versionId: number
}) {
	const xhr = useXHR()
	const {
		data: policy,
		error,
		isLoading,
	} = useSWR<TCancellationPolicy, Error>(
		`/cancellation-policies/${versionId}`,
		() => cancellationPoliciesXHR(xhr).getVersionById(versionId)
	)

	if (isLoading) {
		return (
			<Stack
				padding="6"
				justifyContent="center"
				alignItems="center"
				style={{ minHeight: "20rem" }}
			>
				<Spinner />
			</Stack>
		)
	}
	if (error) {
		return (
			<Alert status="error">
				{error.message || "Failed to load policy details. Please try again."}
			</Alert>
		)
	}
	if (!policy) {
		return (
			<Stack
				padding="6"
				justifyContent="center"
				alignItems="center"
				style={{ minHeight: "20rem" }}
			>
				<Text color="muted" fontWeight="semibold">
					No Policy Found.
				</Text>
			</Stack>
		)
	}

	const baseRule = policy.rules.find(isBaseDateRangeRule)
	const dateRules = policy.rules.filter((r) => !isBaseDateRangeRule(r))

	return (
		<Stack gap="4">
			<Inline gap="8" flexWrap="wrap">
				<Stack gap="px">
					<Text fontSize="sm">Created on</Text>
					<Time color="muted" timestamp={policy.created_at} dateFormat />
				</Stack>
				<Stack gap="px">
					<Text fontSize="sm">Created by</Text>
					<Text color="muted">{policy.created_by?.name}</Text>
				</Stack>
			</Inline>
			{baseRule ? <PolicyRuleCard rule={baseRule} isBase /> : null}
			{dateRules.map((rule) => (
				<PolicyRuleCard key={rule.id} rule={rule} isBase={false} />
			))}
			{policy.additional_info ? (
				<Stack gap="2">
					<Text fontWeight="semibold">Additional information</Text>
					<Markdown>{policy.additional_info}</Markdown>
				</Stack>
			) : null}
		</Stack>
	)
}

export function PolicyRuleCard({
	rule,
	isBase,
}: {
	rule: TCancellationPolicyRule
	isBase: boolean
}) {
	return (
		<Box borderWidth="1" rounded="md">
			<Stack gap="4" bgColor="white" rounded="md">
				{isBase ? (
					<Inline
						gap="2"
						alignItems="center"
						padding="3"
						bgColor="success"
						borderBottomWidth="1"
						roundedTop="md"
					>
						<Text fontWeight="semibold" fontSize="md">
							Base Rules
						</Text>
						<Tooltip content="Applies when no date range matches the check-in date">
							<Box>
								<Icons.Info />
							</Box>
						</Tooltip>
					</Inline>
				) : (
					<Box padding="3" bgColor="primary" borderBottomWidth="1">
						<Text fontWeight="semibold" fontSize="md">
							{formatDateRange(
								rule.valid_from ? parseDate(rule.valid_from) : null,
								rule.valid_to ? parseDate(rule.valid_to) : null
							)}{" "}
							(Check-in)
						</Text>
					</Box>
				)}

				<Stack as="ul" gap="1" paddingX="6">
					{rule.conditions.map((c) => {
						return (
							<Box as="li" key={c.id}>
								<Text fontSize="sm">
									• Cancellation made between {c.min_days_before_checkin} and{" "}
									{c.max_days_before_checkin} days prior to check-in will incur
									a penalty of{" "}
									{c.cancellation_percentage == null
										? "—"
										: `${c.cancellation_percentage}%`}{" "}
									of the booking value.
								</Text>
							</Box>
						)
					})}
				</Stack>
				<Box paddingX="6" paddingBottom="6">
					<PenaltyTimelineDisplay
						conditions={rule.conditions.map((c) => ({
							min_days_before_checkin: c.min_days_before_checkin,
							max_days_before_checkin: c.max_days_before_checkin,
							penalty_value: Number(c.cancellation_percentage),
						}))}
					/>
				</Box>
			</Stack>
		</Box>
	)
}

export { DiffPill }

export function VersionHistoryDialog({
	detailId,
	currentVersionId,
	currentVersion,
	children,
}: {
	detailId: number
	currentVersionId: number
	currentVersion: number
	children: (props: { open: () => void }) => React.ReactNode
}) {
	const xhr = useXHR()

	return (
		<GenericVersionHistoryDialog<TCancellationPolicy>
			detailId={detailId}
			currentVersionId={currentVersionId}
			currentVersion={currentVersion}
			versionsKey={(id) =>
				`/cancellation-policy-heads/${id}/cancellation-policies`
			}
			fetchVersions={(id) =>
				cancellationPoliciesXHR(xhr).getVersions(id, { page: 1, limit: 100 })
			}
			versionKey={(id) => `/cancellation-policies/${id}`}
			fetchVersion={(id) => cancellationPoliciesXHR(xhr).getVersionById(id)}
			renderDiffEntries={(previous, current) => {
				const entries = diffPolicyRules(
					previous.rules || [],
					current.rules || []
				)
				return {
					hasChanges: entries.length > 0,
					entries: entries.map((entry) => (
						<RuleDiffCard key={entry.key} entry={entry} />
					)),
				}
			}}
		>
			{children}
		</GenericVersionHistoryDialog>
	)
}

function RuleDiffCard({ entry }: { entry: TPolicyRuleDiffEntry }) {
	return (
		<Box borderWidth="1" rounded="md" overflow="hidden">
			<Box
				display="grid"
				overflowX="auto"
				style={{ gridTemplateColumns: "1fr 1fr" }}
			>
				<RuleHeaderCell
					rule={entry.previous}
					status={entry.status}
					side="previous"
				/>
				<RuleHeaderCell
					rule={entry.current}
					status={entry.status}
					side="current"
				/>
				{entry.items.map((row) => (
					<Fragment key={row.key}>
						<ConditionCell
							condition={row.previous}
							status={row.status}
							side="previous"
						/>
						<ConditionCell
							condition={row.current}
							status={row.status}
							side="current"
						/>
					</Fragment>
				))}
			</Box>
		</Box>
	)
}

function RuleHeaderCell({
	rule,
	status,
	side,
}: {
	rule: TCancellationPolicyRule | null
	status: TDiffStatus
	side: TDiffSide
}) {
	const tone =
		status === "added" || status === "removed" ? diffTone(status, side) : null
	return (
		<Box padding="2" bgColor="subtle" style={{ minWidth: "20rem" }}>
			{rule ? (
				<DiffPill tone={tone} fontWeight="semibold">
					{formatDateRangeRuleLabel(rule)}
				</DiffPill>
			) : null}
		</Box>
	)
}

function ConditionCell({
	condition,
	status,
	side,
}: {
	condition: TCancellationPolicyCondition | null
	status: TDiffStatus
	side: TDiffSide
}) {
	return (
		<Box
			paddingX="2"
			paddingY="3"
			borderTopWidth="1"
			style={{ minWidth: "20rem" }}
		>
			{condition ? (
				<DiffPill
					tone={diffTone(status, side)}
					fontSize="sm"
					display="inlineBlock"
				>
					{formatPolicyConditionLabel(condition)}
				</DiffPill>
			) : null}
		</Box>
	)
}
